/**
 * useBundleItemCodes — Batch-check which item codes have active Product Bundle records.
 *
 * One API call per page load (not N+1). Returns a stable Set<string> of bundle item codes.
 * Uses `frappe.client.get_list` (not `get_all`) for tenant isolation.
 *
 * Plan: 171-bundle-product-ux, Epic 02
 */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "@/lib/store/authStore";
import { frappeMethod } from "@/lib/erp/utils";

interface ProductBundleRow {
  new_item_code: string;
}

/**
 * Fetch bundle status for a specific set of item codes (detail page use).
 */
export function useBundleItemCodes(itemCodes: string[]) {
  const tenant = useAuthStore((s) => s.currentTenant);

  // Sort for stable query key (item order may vary between renders)
  const sortedCodes = useMemo(
    () => [...itemCodes].sort(),
    [itemCodes],
  );

  const { data: bundleItemCodes = EMPTY_SET, isLoading } = useQuery({
    queryKey: ["Product Bundle", "batch-check", tenant, sortedCodes],
    queryFn: () =>
      frappeMethod<ProductBundleRow[]>("frappe.client.get_list", {
        doctype: "Product Bundle",
        filters: {
          new_item_code: ["in", sortedCodes],
          disabled: 0,
        },
        fields: ["new_item_code"],
        limit_page_length: 0,
      }),
    enabled: sortedCodes.length > 0 && !!tenant,
    staleTime: 60_000,
    select: (data): Set<string> =>
      new Set((data || []).map((row) => row.new_item_code)),
  });

  return { bundleItemCodes, isLoading };
}

/**
 * Fetch ALL active Product Bundle item codes for the current tenant.
 * Used by list views where we can't easily extract item codes before rendering.
 * Product Bundles are typically < 100 per tenant, so this is cheap.
 */
export function useAllBundleItemCodes() {
  const tenant = useAuthStore((s) => s.currentTenant);

  const { data: bundleItemCodes = EMPTY_SET, isLoading } = useQuery({
    queryKey: ["Product Bundle", "all-active", tenant],
    queryFn: () =>
      frappeMethod<ProductBundleRow[]>("frappe.client.get_list", {
        doctype: "Product Bundle",
        filters: { disabled: 0 },
        fields: ["new_item_code"],
        limit_page_length: 0,
      }),
    enabled: !!tenant,
    staleTime: 60_000,
    select: (data): Set<string> =>
      new Set((data || []).map((row) => row.new_item_code)),
  });

  return { bundleItemCodes, isLoading };
}

const EMPTY_SET = new Set<string>();
